import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Camera,
  Loader2,
  Trash2,
  X,
  Check,
  ImagePlus,
  ArrowRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

type Phase = "idle" | "picking" | "uploading" | "ready" | "error";

type Classified = {
  highlights: string[];
  shipped: string[];
  blockers: string[];
};

const MAX_BATCH_IMAGES = 6;

/** Read a File as a base64 string (no data: prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const url = fr.result as string;
      const comma = url.indexOf(",");
      resolve(comma >= 0 ? url.slice(comma + 1) : url);
    };
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
}

/**
 * Photo → OCR → classified bullets, with **mandatory user review
 * before commit**. Mirrors VoiceCaptureDialog so both pipelines
 * share the same trust model: a misread by the vision model can
 * never silently insert signals.
 *
 * Flow:
 *   1. idle    → pick a file (camera roll, gallery, or live capture)
 *   2. uploading → server runs OCR + classification
 *   3. ready   → render the extracted text + editable item lists
 *   4. user can remove items, edit text, then commits via
 *      `photo.applyMemo`. Closing without committing fires
 *      `photo.discardMemo` so the staged row doesn't leak.
 */
export function PhotoCaptureDialog({
  open,
  onOpenChange,
  onLogged,
  occurredOn,
  onApply,
  workspaceTeamId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onLogged: () => void;
  /** Pin signals to noon UTC of this day. Same semantic as voice. */
  occurredOn?: string;
  /**
   * The ACTIVE workspace the capture belongs to. Threaded through to
   * `photo.applyMemo` so photo captures land where the user is looking,
   * exactly like typed capture. Unset (e.g. EDITOR mode, which inserts no
   * signals) → server keeps its primary-team fallback.
   */
  workspaceTeamId?: number | null;
  /**
   * EDITOR mode. When provided, Save hands the OCR'd items back to the caller
   * (to add to a receipt's content) INSTEAD of creating private signals; the
   * pending memo is discarded so nothing is double-recorded.
   */
  onApply?: (result: { highlights: string[]; shipped: string[]; blockers: string[] }) => void;
}) {
  const { locale } = useI18n();
  const [phase, setPhase] = useState<Phase>("idle");
  // Multi-frame staging. Each frame is held as both a File (for upload)
  // and an object URL (for thumbnail preview). Object URLs are
  // revoked when frames are removed or the dialog closes, so memory
  // doesn't leak across multiple captures within one session.
  const [frames, setFrames] = useState<
    Array<{ file: File; previewUrl: string }>
  >([]);
  const [extracted, setExtracted] = useState("");
  const [highlights, setHighlights] = useState<string[]>([]);
  const [shipped, setShipped] = useState<string[]>([]);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [memoId, setMemoId] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const applyMutation = trpc.photo.applyMemo.useMutation();
  const discardMutation = trpc.photo.discardMemo.useMutation();

  // Reset state every time the dialog opens; cancel any pending
  // memo on close so we don't leave orphans.
  useEffect(() => {
    if (!open) {
      if (memoId !== null && phase === "ready") {
        discardMutation.mutate({ memoId });
      }
      setPhase("idle");
      for (const f of frames) URL.revokeObjectURL(f.previewUrl);
      setFrames([]);
      setExtracted("");
      setHighlights([]);
      setShipped([]);
      setBlockers([]);
      setMemoId(null);
      setErrorMsg("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // On mobile, opening the OS photo picker / camera blurs the page, which makes
  // Radix think focus moved "outside" the dialog and auto-closes it (and a stray
  // tap can then dismiss the Capture sheet too → back to Home). While the picker
  // is up we suppress dialog close; we lift the suppression once focus returns.
  const suppressCloseRef = useRef(false);
  function pickFile() {
    suppressCloseRef.current = true;
    const release = () => {
      // Small delay so the focus-return event that fires alongside the picker
      // dismissal is also covered before we re-enable outside-close.
      window.setTimeout(() => {
        suppressCloseRef.current = false;
      }, 600);
      window.removeEventListener("focus", release);
    };
    window.addEventListener("focus", release);
    fileInputRef.current?.click();
  }

  // Mobile camera / "share" flows often hand us a file with an EMPTY or generic
  // MIME type (e.g. "" or "application/octet-stream"), so a strict
  // `type.startsWith("image/")` check silently drops the photo and the user sees
  // "nothing happen". Accept anything that is an image by MIME, by extension, or
  // a typeless camera capture; the server re-validates the bytes anyway.
  function looksLikeImage(file: File): boolean {
    if (file.type.startsWith("image/")) return true;
    if (/\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(file.name || "")) return true;
    // Typeless capture from a camera input we explicitly opened with accept=image/*.
    return file.type === "" || file.type === "application/octet-stream";
  }

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selection of the same file later
    if (picked.length === 0) return;
    setErrorMsg("");
    // Filter to images, dedupe by name+size, cap at MAX_BATCH_IMAGES.
    setFrames(prev => {
      const next = [...prev];
      for (const file of picked) {
        if (!looksLikeImage(file)) {
          toast.error(
            locale === "ar"
              ? "الرجاء اختيار صورة"
              : "Please pick an image file",
          );
          continue;
        }
        const dup = next.find(
          f => f.file.name === file.name && f.file.size === file.size,
        );
        if (dup) continue;
        if (next.length >= MAX_BATCH_IMAGES) {
          toast(
            locale === "ar"
              ? `الحدّ الأقصى ${MAX_BATCH_IMAGES} صور`
              : `Max ${MAX_BATCH_IMAGES} photos per batch`,
          );
          break;
        }
        next.push({ file, previewUrl: URL.createObjectURL(file) });
      }
      return next;
    });
    setPhase("picking");
  }

  function removeFrame(index: number) {
    setFrames(prev => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) setPhase("idle");
      return next;
    });
  }

  async function runOcr() {
    if (frames.length === 0) return;
    setErrorMsg("");
    setPhase("uploading");
    try {
      const payload = {
        images: await Promise.all(
          frames.map(async f => ({
            data: await fileToBase64(f.file),
            mimeType: f.file.type || "image/jpeg",
          })),
        ),
      };
      const res = await fetch("/api/photo/upload-batch", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "content-type": "application/json",
          "x-locale": locale,
        },
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(`Upload failed (${res.status}) ${detail.slice(0, 200)}`);
      }
      const data = (await res.json()) as {
        memoId: number | null;
        extractedText: string;
        classified: Classified;
      };
      setExtracted(data.extractedText);
      setHighlights(data.classified.highlights);
      setShipped(data.classified.shipped);
      setBlockers(data.classified.blockers);
      setMemoId(data.memoId);
      setPhase("ready");
    } catch (err: any) {
      setErrorMsg(err?.message || "Photo processing failed");
      setPhase("error");
    }
  }

  async function commit() {
    if (memoId === null) return;
    // Trim + drop empties so a row left blank during edit doesn't insert a
    // blank item. Mirrors the server-side parser defense.
    const clean = (xs: string[]) =>
      xs.map(s => s.trim()).filter(s => s.length > 0);
    // EDITOR mode: hand the OCR'd items to the caller and discard the pending
    // memo (no signal is created).
    if (onApply) {
      onApply({
        highlights: clean(highlights),
        shipped: clean(shipped),
        blockers: clean(blockers),
      });
      onOpenChange(false); // close-handler discards the pending memo
      return;
    }
    try {
      const r = await applyMutation.mutateAsync({
        memoId,
        highlights: clean(highlights),
        shipped: clean(shipped),
        blockers: clean(blockers),
        ...(occurredOn ? { occurredOn } : {}),
        workspaceTeamId,
      });
      if (r.inserted > 0) {
        onLogged();
        toast.success(
          locale === "ar"
            ? `أُضيف ${r.inserted} عنصراً`
            : `Added ${r.inserted} ${r.inserted === 1 ? "item" : "items"}`,
        );
      } else {
        toast(
          locale === "ar"
            ? "لم يُضف شيء"
            : "Nothing to add",
        );
      }
      setMemoId(null); // mark consumed so close-handler doesn't discard
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || "Couldn't save");
    }
  }

  // Live count of "would-be-inserted" rows (matches the trim-and-drop
  // logic in commit()) so the Save button reflects the real outcome
  // instead of including empty-during-edit rows.
  const liveCount = (xs: string[]) =>
    xs.reduce((n, s) => (s.trim().length > 0 ? n + 1 : n), 0);
  const totalItems =
    liveCount(highlights) + liveCount(shipped) + liveCount(blockers);
  const hasAnything = extracted.trim().length > 0 || totalItems > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        // Ignore the spurious "close" Radix fires while the OS picker is open.
        if (!next && suppressCloseRef.current) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        showCloseButton={false}
        onInteractOutside={e => {
          if (suppressCloseRef.current) e.preventDefault();
        }}
        onEscapeKeyDown={e => {
          if (suppressCloseRef.current) e.preventDefault();
        }}
        className="
          sm:max-w-xl sm:max-h-[90dvh]
          max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0
          max-sm:w-full max-sm:max-w-none max-sm:h-[100dvh] max-sm:max-h-[100dvh]
          max-sm:rounded-b-none max-sm:rounded-t-2xl
          max-sm:pt-[env(safe-area-inset-top)] max-sm:pb-[env(safe-area-inset-bottom)]
          flex flex-col p-0 gap-0 overflow-hidden
        "
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onFileSelected}
          className="hidden"
        />

        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-3 pb-3 shrink-0">
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Camera className="h-5 w-5 text-coral" />
            {locale === "ar" ? "التقاط صورة" : "Photo capture"}
          </DialogTitle>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={locale === "ar" ? "إغلاق" : "Close"}
            className="shrink-0 h-9 w-9 grid place-items-center rounded-full border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 space-y-4">
          {phase === "idle" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {locale === "ar"
                  ? `اختر حتى ${MAX_BATCH_IMAGES} صور من المعرض أو الكاميرا (مفيد للسبورات الكبيرة على عدّة لقطات). سنقرأ النص من كلّ الصور دفعة واحدة ونصنّفه إلى إنجازات وأُنجِز وعوائق. راجع قبل الحفظ.`
                  : `Pick up to ${MAX_BATCH_IMAGES} photos from gallery or camera (handy for big whiteboards that span several shots). We read all of them in one pass and sort into highlights, shipped, and blockers. You review before anything is saved.`}
              </p>
              <button
                type="button"
                onClick={pickFile}
                className="w-full flex flex-col items-center justify-center gap-3 py-12 rounded-2xl border-2 border-dashed border-coral/40 bg-coral-soft/20 hover:bg-coral-soft/40 transition-colors"
              >
                <div className="rounded-full bg-coral text-white p-3">
                  <ImagePlus className="h-6 w-6" />
                </div>
                <div className="text-sm font-medium text-foreground">
                  {locale === "ar" ? "اختر صوراً" : "Pick photo(s)"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {locale === "ar"
                    ? "السبورة، ملاحظات، شرائح، لقطة شاشة…"
                    : "Whiteboard, notes, slides, screenshot…"}
                </div>
              </button>
            </div>
          )}

          {/* Staging strip: shown during picking, uploading, and
              ready phases so the user always knows which frames went
              into the OCR call. Horizontal scroll keeps it tidy on
              mobile even with 6 frames. */}
          {(phase === "picking" || phase === "uploading" || phase === "ready") &&
            frames.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium flex items-center justify-between">
                  <span>
                    {locale === "ar"
                      ? `الصور (${frames.length}/${MAX_BATCH_IMAGES})`
                      : `Photos (${frames.length}/${MAX_BATCH_IMAGES})`}
                  </span>
                  {phase === "picking" && frames.length < MAX_BATCH_IMAGES && (
                    <button
                      type="button"
                      onClick={pickFile}
                      className="text-coral hover:underline normal-case tracking-normal text-xs"
                    >
                      {locale === "ar" ? "+ إضافة المزيد" : "+ Add more"}
                    </button>
                  )}
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                  {frames.map((f, i) => (
                    <div
                      key={f.previewUrl}
                      className="relative shrink-0 w-24 h-24 rounded-lg overflow-hidden border border-border bg-muted/30"
                    >
                      <img
                        src={f.previewUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      {/* Frame index badge — helps the user keep track
                          when shooting a multi-frame whiteboard in
                          left-to-right order. */}
                      <div className="absolute top-1 start-1 text-[10px] font-medium bg-black/55 text-white rounded px-1.5 py-0.5">
                        {i + 1}
                      </div>
                      {phase === "picking" && (
                        <button
                          type="button"
                          onClick={() => removeFrame(i)}
                          aria-label={locale === "ar" ? "حذف" : "Remove"}
                          className="absolute top-0.5 end-0.5 h-5 w-5 grid place-items-center rounded-full bg-black/55 hover:bg-black/75 text-white"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {phase === "picking" && (
            <Button
              onClick={runOcr}
              disabled={frames.length === 0}
              className="w-full bg-coral hover:bg-coral/90 text-white gap-1.5"
            >
              <Check className="h-4 w-4" />
              {locale === "ar"
                ? frames.length === 1
                  ? "قراءة الصورة"
                  : `قراءة ${frames.length} صور`
                : frames.length === 1
                  ? "Read this photo"
                  : `Read ${frames.length} photos`}
            </Button>
          )}

          {phase === "uploading" && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {locale === "ar"
                ? frames.length === 1
                  ? "جاري قراءة الصورة وتصنيفها…"
                  : `جاري قراءة ${frames.length} صور ودمج النتائج…`
                : frames.length === 1
                  ? "Reading the image and classifying…"
                  : `Reading ${frames.length} photos and merging…`}
            </div>
          )}

          {phase === "error" && (
            <div className="rounded-lg border border-red-300/40 bg-red-50/60 dark:bg-red-900/20 p-3 text-sm">
              <div className="font-medium text-red-700 mb-1">
                {locale === "ar"
                  ? "فشلت معالجة الصورة"
                  : "Couldn't process the photo"}
              </div>
              <div className="text-xs text-muted-foreground">{errorMsg}</div>
              <Button
                onClick={pickFile}
                size="sm"
                variant="outline"
                className="mt-3"
              >
                {locale === "ar" ? "إعادة المحاولة" : "Try again"}
              </Button>
            </div>
          )}

          {phase === "ready" && (
            <>
              {!hasAnything && (
                <div className="rounded-lg border border-amber-300/40 bg-amber-50/60 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
                  {locale === "ar"
                    ? "لم نجد نصاً قابلاً للقراءة في الصورة. جرب صورة أوضح أو أقرب."
                    : "We didn't find any readable text. Try a closer or sharper photo."}
                </div>
              )}

              {extracted !== "" && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5">
                    {locale === "ar" ? "النص المُستخرج (قابل للتحرير)" : "Extracted text (editable)"}
                  </div>
                  {/* Free-form edit area for the raw OCR output. The
                      classified bullets below are independent — editing
                      this text doesn't re-run the classifier; it just
                      lets the user fix typos before commit. The text
                      itself isn't persisted as a signal, it's archived
                      on the photoMemo row for audit. */}
                  <textarea
                    value={extracted}
                    onChange={e => setExtracted(e.target.value)}
                    rows={Math.min(8, Math.max(3, extracted.split("\n").length))}
                    className="w-full text-xs bg-muted/30 rounded-lg p-3 max-h-[200px] resize-y whitespace-pre-wrap break-words text-foreground/80 border border-border/40 focus:border-coral focus:outline-none focus:ring-1 focus:ring-coral/40"
                  />
                </div>
              )}

              <ItemList
                title={locale === "ar" ? "إنجازات" : "Highlights"}
                items={highlights}
                onChange={setHighlights}
                tone="coral"
                category="highlight"
                onReclassify={(idx, target) =>
                  moveBetweenLists(
                    "highlight",
                    target,
                    idx,
                    highlights,
                    setHighlights,
                    shipped,
                    setShipped,
                    blockers,
                    setBlockers,
                  )
                }
              />
              <ItemList
                title={locale === "ar" ? "أُنجِز" : "Shipped"}
                items={shipped}
                onChange={setShipped}
                tone="amber"
                category="shipped"
                onReclassify={(idx, target) =>
                  moveBetweenLists(
                    "shipped",
                    target,
                    idx,
                    highlights,
                    setHighlights,
                    shipped,
                    setShipped,
                    blockers,
                    setBlockers,
                  )
                }
              />
              <ItemList
                title={locale === "ar" ? "عوائق" : "Blockers"}
                items={blockers}
                onChange={setBlockers}
                tone="red"
                category="blocker"
                onReclassify={(idx, target) =>
                  moveBetweenLists(
                    "blocker",
                    target,
                    idx,
                    highlights,
                    setHighlights,
                    shipped,
                    setShipped,
                    blockers,
                    setBlockers,
                  )
                }
              />

              <div className="text-[11px] text-muted-foreground italic">
                {locale === "ar"
                  ? "احذف أيّ عنصر غير صحيح قبل الحفظ. نراجع كل شيء قبل الإدراج."
                  : "Remove anything that's off before saving. Nothing is logged until you click below."}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {phase === "ready" && (
          <div className="px-5 py-3 shrink-0 border-t border-border/60 flex items-center gap-2">
            <Button
              onClick={commit}
              disabled={applyMutation.isPending || totalItems === 0}
              className="bg-coral hover:bg-coral/90 text-white gap-1.5"
            >
              {applyMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {locale === "ar"
                ? `حفظ ${totalItems} عنصراً`
                : `Save ${totalItems} ${totalItems === 1 ? "item" : "items"}`}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                // "Start over" — drop current memo (will discard on
                // close-effect) and go back to the picker. Simpler
                // than trying to splice new frames into an already-
                // OCR'd batch, and matches user intent ("the read
                // was wrong, let me re-shoot").
                for (const f of frames) URL.revokeObjectURL(f.previewUrl);
                setFrames([]);
                setExtracted("");
                setHighlights([]);
                setShipped([]);
                setBlockers([]);
                if (memoId !== null) discardMutation.mutate({ memoId });
                setMemoId(null);
                setPhase("idle");
                setTimeout(() => pickFile(), 0);
              }}
              disabled={applyMutation.isPending}
              className="gap-1.5"
            >
              <Camera className="h-4 w-4" />
              {locale === "ar" ? "إعادة" : "Redo"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

type Category = "highlight" | "shipped" | "blocker";
// Buckets aligned with the receipt's sections.

/**
 * Move an item between the three lists at the dialog level. Called
 * by ItemList's reclassify menu. Pure functional updates per list
 * so React batches the two state changes correctly.
 */
function moveBetweenLists(
  from: Category,
  to: Category,
  idx: number,
  highlights: string[],
  setHighlights: (xs: string[]) => void,
  shipped: string[],
  setShipped: (xs: string[]) => void,
  blockers: string[],
  setBlockers: (xs: string[]) => void,
) {
  if (from === to) return;
  const getList = (c: Category) =>
    c === "highlight" ? highlights : c === "shipped" ? shipped : blockers;
  const setList = (c: Category, xs: string[]) =>
    c === "highlight"
      ? setHighlights(xs)
      : c === "shipped"
        ? setShipped(xs)
        : setBlockers(xs);
  const sourceList = getList(from);
  const item = sourceList[idx];
  if (!item) return;
  setList(
    from,
    sourceList.filter((_, i) => i !== idx),
  );
  setList(to, [...getList(to), item]);
}

/**
 * Editable item list. Each item is a row with:
 *   - text (inline editable textarea)
 *   - "Move →" menu (reclassify into another category — turns a Task
 *     into a Meeting/Blocker without delete-and-retype, premium UX)
 *   - trash button (drop the item from the commit)
 *
 * `onReclassify(idx, target)` fires when the user picks a different
 * category from the menu. The dialog-level moveBetweenLists handler
 * performs the actual splice across the three lists.
 */
function ItemList({
  title,
  items,
  onChange,
  tone,
  category,
  onReclassify,
}: {
  title: string;
  items: string[];
  onChange: (next: string[]) => void;
  tone: "coral" | "amber" | "red";
  category: Category;
  onReclassify: (idx: number, target: Category) => void;
}) {
  const { locale } = useI18n();
  if (items.length === 0) return null;
  const dotClass =
    tone === "coral"
      ? "bg-coral"
      : tone === "amber"
        ? "bg-yellow-400"
        : "bg-red-500";
  // The two "other" categories this row can be reclassified into.
  // Skip self so the menu only shows productive moves.
  const others: Array<{ key: Category; label: string }> = (
    [
      { key: "highlight" as Category, label: locale === "ar" ? "إنجاز" : "Highlight" },
      { key: "shipped" as Category, label: locale === "ar" ? "أُنجِز" : "Shipped" },
      { key: "blocker" as Category, label: locale === "ar" ? "عائق" : "Blocker" },
    ]
  ).filter(o => o.key !== category);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5 flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
        {title}
        <span className="opacity-60">({items.length})</span>
      </div>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-sm rounded-md border border-border/60 bg-background px-2.5 py-1.5 focus-within:border-coral focus-within:ring-1 focus-within:ring-coral/40"
          >
            <textarea
              value={item}
              onChange={e => {
                const next = items.slice();
                next[i] = e.target.value;
                onChange(next);
              }}
              rows={1}
              className="flex-1 min-w-0 bg-transparent resize-none outline-none text-sm leading-snug break-words"
              onInput={e => {
                const el = e.currentTarget;
                el.style.height = "auto";
                el.style.height = el.scrollHeight + "px";
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={
                    locale === "ar" ? "إعادة التصنيف" : "Reclassify"
                  }
                  title={
                    locale === "ar" ? "إعادة التصنيف" : "Move to…"
                  }
                  className="shrink-0 p-1 rounded text-muted-foreground hover:text-coral hover:bg-coral/10 transition-colors"
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[7rem]">
                {others.map(o => (
                  <DropdownMenuItem
                    key={o.key}
                    onSelect={() => onReclassify(i, o.key)}
                  >
                    {locale === "ar" ? "نقل إلى " : "Move to "}
                    {o.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              onClick={() =>
                onChange(items.filter((_, idx) => idx !== i))
              }
              aria-label={locale === "ar" ? "حذف" : "Remove"}
              className="shrink-0 p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
